import { cn } from '@/lib/utils';

/**
 * Kanfy logo — a small house sitting inside a single cupped curve.
 *
 * The curve is an abstracted open palm, the gesture of holding. It is not a
 * hand: anatomical fingers do not survive scale reduction, embroidery, or
 * single-colour print. Every corner is rounded, including the roof apex.
 *
 * Colour assignment carries meaning and is not decorative — the house is
 * teal (the home), the curve is clay (the helper), the windows are amber
 * (the home is occupied and lit). Two parties plus a warm outcome. Only the
 * `mono` variant flattens this, and only because embroidery and rubber
 * stamps cannot hold more than one colour.
 *
 * Geometry is fixed. Do not redraw the paths.
 */

export type KanfyLogoVariant =
  /**
   * Follows the theme via CSS custom properties: full colour on the cream
   * nav, reversed on the ink one. Prefer this for in-app chrome — picking
   * the variant from a JS theme value gets it wrong whenever the class and
   * the React state disagree, which is exactly what happened when the
   * `.dark` class was set outside the ThemeProvider.
   */
  | 'auto'
  /** Full colour, for cream or white grounds. */
  | 'primary'
  /** House and wordmark go cream, curve stays clay. For ink or teal grounds. */
  | 'reversed'
  /** Everything ink, windows and door knock out. Embroidery, stamps. */
  | 'mono-ink'
  /** Everything cream, windows and door knock out. Dark uniforms, vinyl. */
  | 'mono-cream';

/**
 * Named size scale for the mark, in px. Use these across the app instead of
 * ad-hoc numbers so the logo is consistent everywhere it appears:
 *   xs favicon/tight chrome · sm compact nav & footer · md default header ·
 *   lg auth & cards · xl marketing/hero.
 * `size` still accepts a raw number for one-off needs.
 */
export const KANFY_LOGO_SIZES = {
  xs: 24,
  sm: 28,
  md: 36,
  lg: 48,
  xl: 72,
} as const;

export type KanfyLogoSize = keyof typeof KANFY_LOGO_SIZES;

interface KanfyLogoProps {
  variant?: KanfyLogoVariant;
  /**
   * Rendered height of the mark: a named scale step ('xs'…'xl') or a raw px
   * number. The wordmark scales from this. Prefer the scale.
   */
  size?: KanfyLogoSize | number;
  /** Show the `kanfy` wordmark beside the mark. */
  showWordmark?: boolean;
  /**
   * Drop the windows and door. Required below ~40mm print / 32px screen,
   * where the detail fills in. Applied automatically under 32px.
   */
  simplified?: boolean;
  /** Knockout colour for mono variants — should match the ground behind it. */
  knockout?: string;
  className?: string;
}

const INK = '#0D3A31';
const TEAL = '#12735C';
const CLAY = '#D9663D';
const AMBER = '#F2A93B';
const CREAM = '#FBF3E7';

interface MarkColors {
  house: string;
  curve: string;
  window: string;
  door: string;
  word: string;
}

function colorsFor(variant: KanfyLogoVariant, knockout: string): MarkColors {
  switch (variant) {
    case 'auto':
      // Defined in index.css for :root and .dark, so the mark flips with the
      // cascade and needs no JS. The curve stays clay in both.
      return {
        house: 'hsl(var(--logo-house))',
        curve: 'hsl(var(--kanfy-clay))',
        window: 'hsl(var(--kanfy-amber))',
        door: 'hsl(var(--logo-door))',
        word: 'hsl(var(--logo-house))',
      };
    case 'reversed':
      return { house: CREAM, curve: CLAY, window: AMBER, door: knockout, word: CREAM };
    case 'mono-ink':
      return { house: INK, curve: INK, window: knockout, door: knockout, word: INK };
    case 'mono-cream':
      return { house: CREAM, curve: CREAM, window: knockout, door: knockout, word: CREAM };
    case 'primary':
    default:
      return { house: TEAL, curve: CLAY, window: AMBER, door: CREAM, word: TEAL };
  }
}

export function KanfyLogo({
  variant = 'primary',
  size = 'md',
  showWordmark = true,
  simplified,
  knockout,
  className,
}: KanfyLogoProps) {
  // Resolve a named scale step to px; a raw number passes through.
  const px = typeof size === 'number' ? size : KANFY_LOGO_SIZES[size];
  // Below the minimum size the windows and door fill in, so drop them
  // whether or not the caller remembered to.
  const dropDetail = simplified ?? px < 32;

  const defaultKnockout = variant === 'mono-cream' || variant === 'reversed' ? INK : CREAM;
  const c = colorsFor(variant, knockout ?? defaultKnockout);

  return (
    <span
      className={cn('inline-flex items-center', className)}
      // Gap is 40% of mark width, per the brand spec's lockup metrics.
      style={{ gap: showWordmark ? px * 0.4 : undefined }}
    >
      {/* The geometry is in the 110 box, but the curve's stroke reaches
          y≈113, so a 0 0 110 110 viewBox cut its bottom flat — unlike the
          app icon, splash, email header and push icon, which all draw it
          whole. This 116 window (the same 3 / -7.5 offset they use) shows
          the whole mark, centred. Paths unchanged. */}
      <svg
        viewBox="-3 7.5 116 116"
        width={px}
        height={px}
        role="img"
        aria-label="Kanfy"
        style={{ display: 'block', flex: 'none' }}
      >
        <polygon
          points="55,20 85,44 25,44"
          fill={c.house}
          stroke={c.house}
          strokeWidth="9"
          strokeLinejoin="round"
        />
        <rect x="31" y="42" width="48" height="36" rx="9" fill={c.house} />
        {!dropDetail && (
          <>
            <rect x="37" y="50" width="10" height="9" rx="2.5" fill={c.window} />
            <rect x="63" y="50" width="10" height="9" rx="2.5" fill={c.window} />
            <rect x="49" y="60" width="12" height="18" rx="4" fill={c.door} />
          </>
        )}
        <path
          d="M 9 82 C 13 116, 97 116, 101 82"
          fill="none"
          stroke={c.curve}
          strokeWidth="16"
          strokeLinecap="round"
        />
      </svg>

      {showWordmark && (
        <span
          aria-hidden="true"
          style={{
            // Fredoka Medium — the rounded terminals echo the mark's corners
            // (chosen 2026-09-02, "Take B"). Loaded subset-only in index.html;
            // the fallback holds the brand face so an unloaded font barely
            // shifts. Ratio is 0.6 of the mark (Fredoka's tall x-height reads
            // larger than the old Inter stand-in at the same height), tracking
            // -0.01em to open the rounder letterforms slightly.
            fontFamily: "'Fredoka', 'Plus Jakarta Sans', system-ui, sans-serif",
            fontSize: px * 0.6,
            fontWeight: 500,
            letterSpacing: '-0.01em',
            lineHeight: 1,
            color: c.word,
          }}
        >
          kanfy
        </span>
      )}
    </span>
  );
}

export default KanfyLogo;
